
import superagent from 'superagent'
import { readArtemisStream, ArtemisStreamError } from '../../lib/artemis/stream'
import { ARTEMIS_AGENT_ENABLED, ARTEMIS_CLIENT_ACTIONS } from './agent-actions'

const storeFetchUserDataSource = payload => ({
    type: 'FETCH_USER_DATA_SOURCE',
    payload: payload
})

// Rewrite the versioned Artemis base URL to v2. Handles both "/v1" (staging) and
// "/v1.0" (production) suffixes so every environment targets the v2 endpoint.
const ARTEMIS_V2 = __API_URI_ARTEMIS__.replace(/\/v\d+(\.\d+)?$/, '/v2')

const artemisHeaders = (token, json = true) => ({
    'Authorization': `Bearer ${token}`,
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    'Platform': 'Web',
    'Version': `${__version__}`,
})

export const fetchArtemisModels = () => async (dispatch, getState, token) => {
    const response = await fetch(`${ARTEMIS_V2}/query/models`, { headers: artemisHeaders(token) })
    if (!response.ok) return []
    return response.json()
}

// Posts a question and reads the reply stream (WEB-7092). Every stream event goes to
// onEvent; aborting signal stops the request. In agent mode, history and context (the map
// view) go with it (WEB-7098). Resolves with the "done" event and rejects
// with an ArtemisStreamError, or an AbortError when stopped.
// A refused question (API-3797): 429 usage_limit_reached comes with { Error, Message, ResetsAt, Usage },
// kept on the error so the panel can say when the allowance resets. Other failures stay http_<status>.
const sendError = async response => {
    if (response.status === 429) {
        const detail = await response.json().catch(() => null)
        if (detail && detail.Error === 'usage_limit_reached') {
            const err = new ArtemisStreamError('HTTP 429', 'usage_limit_reached')
            err.userMessage = detail.Message || null
            err.resetsAt = detail.ResetsAt || (detail.Usage && detail.Usage.ResetsAt) || null
            err.usage = detail.Usage || null
            // Where credit packs are sold (API-3800); null where they aren't.
            err.buyCreditsUrl = detail.BuyCreditsUrl || null
            return err
        }
    }
    return new ArtemisStreamError(`HTTP ${response.status}`, `http_${response.status}`)
}

const streamArtemisQuery = async (token, body, { onEvent, signal } = {}) => {
    const response = await fetch(`${ARTEMIS_V2}/Query`, {
        method: 'POST',
        headers: artemisHeaders(token),
        body: JSON.stringify(body),
        signal
    })
    if (!response.ok) throw await sendError(response)
    return readArtemisStream(response, onEvent)
}

export const postArtemisQuestion = (question, options = {}, modelKey = null) => async (dispatch, getState, token) => {
    const body = { query: question }
    if (modelKey) body.model = modelKey
    if (ARTEMIS_AGENT_ENABLED) {
        body.agent = true
        body.actions = options.actions || ARTEMIS_CLIENT_ACTIONS
        // Protocol 2 adds step and thinking events (WEB-7094).
        body.protocol = 2
        if (options.history && options.history.length) body.history = options.history
        if (options.context) body.context = options.context
    }
    return streamArtemisQuery(token, body, options)
}

// A user's own Artemis skills (API-3688): how they hunt, followed on later questions.
// Errors carry the server's Error text, written to show the user as-is, as userMessage.
const skillsJson = async (token, path, { method = 'GET', body } = {}) => {
    const response = await fetch(`${ARTEMIS_V3}/${path}`, {
        method,
        headers: artemisHeaders(token, body !== undefined),
        body: body !== undefined ? JSON.stringify(body) : undefined
    })
    if (!response.ok) {
        let problem = null
        try { problem = await response.json() } catch (e) { /* no body */ }
        const err = new ArtemisStreamError(`HTTP ${response.status}`, (problem && problem.Code) || `http_${response.status}`)
        err.status = response.status
        err.userMessage = (problem && (problem.Error || problem.Message)) || null
        throw err
    }
    return response.status === 204 ? null : response.json()
}

// { Items: [Skill], Limits: { MaxEnabled, MaxSkills, MaxNameLength, MaxTitleLength, MaxAppliesWhenLength, MaxBodyLength } }
export const fetchArtemisSkills = () => (dispatch, getState, token) => skillsJson(token, 'Skills')
export const fetchArtemisBuiltInSkills = () => (dispatch, getState, token) => skillsJson(token, 'Skills/BuiltIn')
// One built-in with its Body, for "Make this mine".
export const fetchArtemisBuiltInSkill = name => (dispatch, getState, token) => skillsJson(token, `Skills/BuiltIn/${encodeURIComponent(name)}`)
// { Name, Title, AppliesWhen, Body, Enabled?, BasedOn? } → the Skill (201).
export const createArtemisSkill = skill => (dispatch, getState, token) => skillsJson(token, 'Skills', { method: 'POST', body: skill })
// Partial fields; { Enabled } turns it on or off.
export const updateArtemisSkill = (id, patch) => (dispatch, getState, token) => skillsJson(token, `Skills/${id}`, { method: 'PATCH', body: patch })
export const deleteArtemisSkill = id => (dispatch, getState, token) => skillsJson(token, `Skills/${id}`, { method: 'DELETE' })

// Artemis alerts (API-3840): the alert settings screen. Errors are { Code, Error } like Skills', and every
// route is a 404 where alerts are off (Production for now). Alerts are created in chat (create_alert).
// { Items: [Alert], Live, Limit, AlertsConversationId }, active and paused first.
export const fetchArtemisAlerts = (includeFinished = false) => (dispatch, getState, token) =>
    skillsJson(token, `Alerts${includeFinished ? '?includeFinished=true' : ''}`)
// Any of LeadDays, Milestones, DrawName, Residency, Channels, Title, TimeZone, Paused → the Alert.
export const updateArtemisAlert = (id, patch) => (dispatch, getState, token) => skillsJson(token, `Alerts/${id}`, { method: 'PATCH', body: patch })
export const deleteArtemisAlert = id => (dispatch, getState, token) => skillsJson(token, `Alerts/${id}`, { method: 'DELETE' })
// { Channels, TimeZone, QuietHoursStart, QuietHoursEnd, AvailableChannels, Limit, EmailOff? }
export const fetchArtemisAlertSettings = () => (dispatch, getState, token) => skillsJson(token, 'Alerts/Settings')
// Replaces them all: { Channels, TimeZone, QuietHoursStart, QuietHoursEnd, EmailOn? } → the Settings. EmailOn true
// turns alert emails back on, so send it with the current settings.
export const saveArtemisAlertSettings = settings => (dispatch, getState, token) => skillsJson(token, 'Alerts/Settings', { method: 'PUT', body: settings })

// The name of the view at a point (API-3644): { State, StateName, County, Units[] }, or null
// when the point is outside every state (204). A slow or failed lookup rejects with code
// http_503 (view_unavailable), which is worth one retry.
export const fetchArtemisView = (lat, lng) => async (dispatch, getState, token) => {
    const response = await fetch(`${ARTEMIS_V2}/Query/View?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`, { headers: artemisHeaders(token, false) })
    if (response.status === 204) return null
    if (!response.ok) throw new ArtemisStreamError(`HTTP ${response.status}`, `http_${response.status}`)
    return response.json()
}

// Artemis's speech to text (API-3691): a 16-bit mono WAV of the question, with the query Context
// so misheard unit names can be corrected. Resolves with { Text, Raw, Confidence, DurationMs,
// Corrected }; rejects with code http_404 where the server has no Transcribe yet.
export const transcribeArtemisAudio = (wavBlob, context = null) => async (dispatch, getState, token) => {
    const form = new FormData()
    form.append('audio', wavBlob, 'question.wav')
    if (context) form.append('Context', JSON.stringify(context))
    const response = await fetch(`${ARTEMIS_V2}/Transcribe`, { method: 'POST', headers: artemisHeaders(token, false), body: form })
    if (!response.ok) throw new ArtemisStreamError(`HTTP ${response.status}`, `http_${response.status}`)
    return response.json()
}

// Artemis's read-aloud voice (API-3703): Amazon Polly reading { Text, Voice?, Rate? } as an
// audio/mpeg Blob. Rejects with code http_<status> (502 when Polly is unavailable, so the browser
// voice takes over) or the fetch's own error (an AbortError when the clip is cancelled).
export const speakArtemisText = ({ text, voice, rate }, signal) => async (dispatch, getState, token) => {
    const body = { Text: text }
    if (voice) body.Voice = voice
    if (rate) body.Rate = rate
    const response = await fetch(`${ARTEMIS_V2}/Speech`, { method: 'POST', headers: artemisHeaders(token, true), body: JSON.stringify(body), signal })
    if (!response.ok) throw new ArtemisStreamError(`HTTP ${response.status}`, `http_${response.status}`)
    return response.blob()
}

// { Items: [{ Id, Name, Gender, Engine }], Default }.
export const fetchArtemisVoices = () => async (dispatch, getState, token) => {
    const response = await fetch(`${ARTEMIS_V2}/Speech/Voices`, { headers: artemisHeaders(token, false) })
    if (!response.ok) throw new ArtemisStreamError(`HTTP ${response.status}`, `http_${response.status}`)
    return response.json()
}

// v3 Conversations (API-3641). Saved chats: the server keeps the history, so none is sent.
const ARTEMIS_V3 = __API_URI_ARTEMIS__.replace(/\/v\d+(\.\d+)?$/, '/v3.0')

const v3Json = async (token, path, { method = 'GET', body } = {}) => {
    const response = await fetch(`${ARTEMIS_V3}/${path}`, {
        method,
        headers: artemisHeaders(token, body !== undefined),
        body: body !== undefined ? JSON.stringify(body) : undefined
    })
    if (!response.ok) throw new ArtemisStreamError(`HTTP ${response.status}`, `http_${response.status}`)
    return response.status === 204 || response.status === 202 ? null : response.json()
}

// scratchpad: a session from the start (API-3915), so every device lists it under Sessions before its
// first message. A server without it ignores the field.
export const createArtemisConversation = ({ scratchpad = false } = {}) => (dispatch, getState, token) =>
    v3Json(token, 'Conversations', { method: 'POST', body: { Kind: 'direct', ...(scratchpad && { Scratchpad: true }) } })

// { Items: [Conversation], NextCursor }, newest activity first.
// The alerts conversation alone ({ Items: [it] }, or none yet), by API-3909's kind filter.
export const fetchArtemisAlertsInbox = () => (dispatch, getState, token) =>
    v3Json(token, 'Conversations?kind=alerts&limit=1')

// One Conversation row, with its UnreadCount (the ARTEMIS tab's alerts badge).
export const fetchArtemisConversation = conversationId => (dispatch, getState, token) =>
    v3Json(token, `Conversations/${conversationId}`)

// archived: the archived ones only (the hub's Archived chip). filter (API-3909): { kind } (direct, group
// or alerts) and { scratchpad: true } (sessions only).
export const fetchArtemisConversations = (cursor = null, limit = 20, archived = false, filter = {}) => (dispatch, getState, token) =>
    v3Json(token, `Conversations?limit=${limit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}${archived ? '&archived=true' : ''}`
        + `${filter.kind ? `&kind=${encodeURIComponent(filter.kind)}` : ''}${filter.scratchpad ? '&scratchpad=true' : ''}`)

// { Items: [Message], NextBeforeSeq }, newest first.
export const fetchArtemisConversationMessages = (conversationId, beforeSeq = null, limit = 30) => (dispatch, getState, token) =>
    v3Json(token, `Conversations/${conversationId}/Messages?limit=${limit}${beforeSeq != null ? `&beforeSeq=${beforeSeq}` : ''}`)

export const deleteArtemisConversation = conversationId => (dispatch, getState, token) =>
    v3Json(token, `Conversations/${conversationId}`, { method: 'DELETE' })

export const cancelArtemisMessage = (conversationId, messageId) => (dispatch, getState, token) =>
    v3Json(token, `Conversations/${conversationId}/Messages/${messageId}/Cancel`, { method: 'POST' })

// This month's Artemis use (API-3797): { PeriodStart, ResetsAt, UsedUsd, AllowanceUsd (null: unlimited),
// RemainingUsd, PercentUsed, LimitReached, Replies, InputTokens, OutputTokens }.
export const fetchArtemisUsage = () => (dispatch, getState, token) => v3Detailed(token, 'Usage')

// Group chats (artemisapi docs/client-guide-group-chats.md).
// { Items: [{ UserId, Name, Initials, AvatarUrl, Role, JoinedAt }] }, oldest first.
export const fetchArtemisMembers = conversationId => (dispatch, getState, token) =>
    v3Detailed(token, `Conversations/${conversationId}/Members`)

// Adds the caller's friends (all or none); turns a direct chat into a group. → { Items: [...members] }.
export const addArtemisMembers = (conversationId, userIds) => (dispatch, getState, token) =>
    v3Detailed(token, `Conversations/${conversationId}/Members`, { method: 'POST', body: { UserIds: userIds } })

// Renames (the owner, in a group) or archives for this user: { Title } or { Archived }. → the Conversation.
// patch: any of { Title, Archived, Scratchpad: true } (Scratchpad: API-3921, Open as session).
export const updateArtemisConversation = (conversationId, patch) => (dispatch, getState, token) =>
    v3Detailed(token, `Conversations/${conversationId}`, { method: 'PATCH', body: patch })

// The bell: messages that mention this user, unread ones only →
// { Items: [{ ConversationId, Title, MessageId, Seq, FromUserId, From, Text, At, Read }] }, newest first.
export const fetchArtemisMentions = () => (dispatch, getState, token) =>
    v3Detailed(token, 'Notifications?unread=true&limit=20')

// The owner removes someone, or a member leaves.
export const removeArtemisMember = (conversationId, userId) => (dispatch, getState, token) =>
    v3Detailed(token, `Conversations/${conversationId}/Members/${userId}`, { method: 'DELETE' })

// A 7-day share link: { Id, Token, Url, ExpiresAt }. Url is null where the server has no web app URL.
export const createArtemisInvite = conversationId => (dispatch, getState, token) =>
    v3Detailed(token, `Conversations/${conversationId}/Invites`, { method: 'POST' })

// Joins by link → the Conversation (201 joined, 200 already in); invite_expired (410) when it's gone.
export const acceptArtemisInvite = inviteToken => (dispatch, getState, token) =>
    v3Detailed(token, `Invites/${encodeURIComponent(inviteToken)}/Accept`, { method: 'POST' })

// Group chats: this user is typing (POST, about every 3 s while they type) or cleared the box (DELETE).
// Posting a message stops it on the server anyway. 204 either way.
export const setArtemisTyping = (conversationId, typing) => (dispatch, getState, token) =>
    v3Detailed(token, `Conversations/${conversationId}/Typing`, { method: typing ? 'POST' : 'DELETE' })

// Messages after a Seq, oldest first: { Items, NextAfterSeq, Typing: [{ UserId, Name }] }.
export const pollArtemisMessages = (conversationId, afterSeq) => (dispatch, getState, token) =>
    v3Detailed(token, `Conversations/${conversationId}/Messages?afterSeq=${afterSeq}&limit=100`)

// Marks messages read up to a Seq, which clears the unread and @ badges.
export const markArtemisRead = (conversationId, seq) => (dispatch, getState, token) =>
    v3Detailed(token, `Conversations/${conversationId}/Read`, { method: 'POST', body: { Seq: seq } })

// Who the user follows (the friends panel's list): [{ id, fullName, imageProfileUrl }].
export const fetchArtemisFriends = () => (dispatch, getState, token) =>
    fetch(`${__API_URI_3__}/userFollowing/getAllFollowing`, {
        headers: { Authorization: `Bearer ${token}`, Platform: 'Web', Version: `${__version__}` }
    }).then(r => (r.ok ? r.json() : Promise.reject(new ArtemisStreamError(`HTTP ${r.status}`, `http_${r.status}`))))

// The scratchpad (artemisapi docs/client-guide-scratchpad.md). Errors other than 404 come as
// { Error, Message }; they're thrown with .code and .userMessage so the column can say what happened.
const v3Detailed = async (token, path, { method = 'GET', body } = {}) => {
    const response = await fetch(`${ARTEMIS_V3}/${path}`, {
        method,
        headers: artemisHeaders(token, body !== undefined),
        body: body !== undefined ? JSON.stringify(body) : undefined
    })
    if (!response.ok) {
        const detail = await response.json().catch(() => null)
        const err = new ArtemisStreamError(`HTTP ${response.status}`, (detail && detail.Error) || `http_${response.status}`)
        err.userMessage = detail && detail.Message
        throw err
    }
    return response.status === 204 ? null : response.json()
}

// { Items: [ScratchItem] }: drafts and sent items in Position order.
export const fetchArtemisScratch = conversationId => (dispatch, getState, token) =>
    v3Detailed(token, `Conversations/${conversationId}/Scratch`)

// { Kind, Title, Subtitle?, Geometry?, MarkerTypeId?, Color?, Notes?, Data?, MessageId? } → ScratchItem.
export const addArtemisScratchItem = (conversationId, item) => (dispatch, getState, token) =>
    v3Detailed(token, `Conversations/${conversationId}/Scratch`, { method: 'POST', body: item })

// { Title?, Notes?, MarkerTypeId?, Position? } → ScratchItem; drafts only.
export const updateArtemisScratchItem = (conversationId, itemId, changes) => (dispatch, getState, token) =>
    v3Detailed(token, `Conversations/${conversationId}/Scratch/${itemId}`, { method: 'PATCH', body: changes })

export const deleteArtemisScratchItem = (conversationId, itemId) => (dispatch, getState, token) =>
    v3Detailed(token, `Conversations/${conversationId}/Scratch/${itemId}`, { method: 'DELETE' })

// Saves the items to the user's account in one folder: { FolderId, Sent: [...], Failed: [...] }.
export const sendArtemisScratch = (conversationId, itemIds, folderName = null) => (dispatch, getState, token) =>
    v3Detailed(token, `Conversations/${conversationId}/Scratch/Send`, { method: 'POST', body: { ItemIds: itemIds, FolderName: folderName } })

// { Useful } or { Comment }.
export const artemisMessageFeedback = (conversationId, messageId, feedback) => (dispatch, getState, token) =>
    v3Json(token, `Conversations/${conversationId}/Messages/${messageId}/Feedback`, { method: 'POST', body: feedback })

// Posts a question to a conversation and reads Artemis's reply stream, like the v2 stream plus a
// "message" event with the saved ids. Resolves with the "done" event, or with { message } when the
// server answers a resent ClientMessageId with the finished Message instead of a stream.
export const postArtemisConversationMessage = (conversationId, question, options = {}, modelKey = null) => async (dispatch, getState, token) => {
    const body = { Text: question, ClientMessageId: options.clientMessageId, Actions: options.actions || ARTEMIS_CLIENT_ACTIONS }
    if (options.context) body.Context = options.context
    // Group chats: who the message @mentions, as the composer picked them ([userId | 'artemis']).
    if (Array.isArray(options.mentions) && options.mentions.length) body.Mentions = options.mentions
    if (modelKey) body.Model = modelKey

    const response = await fetch(`${ARTEMIS_V3}/Conversations/${conversationId}/Messages`, {
        method: 'POST',
        headers: artemisHeaders(token),
        body: JSON.stringify(body),
        signal: options.signal
    })
    if (!response.ok) throw await sendError(response)
    if ((response.headers.get('content-type') || '').includes('application/json')) return { message: await response.json() }
    return readArtemisStream(response, options.onEvent)
}

export const dislikeOnSuggestionQuestion = (payload) => (dispatch, getState, token) => {
    return new Promise((resolve, reject) => {
        superagent
            .post(`${__API_URI_ARTEMIS__}/feedback/${payload.queryId}/${payload.documentId}/${payload.useful}`)
            .set('Authorization', `Bearer ${token}`)
            .set('Content-Type', 'application/json')
            .set('Platform', 'Web')
            .set('Version', `${__version__}`)
            .end((err, res) => {
                if (err) {
                    console.log('error while handling api/v1/Query api: ', err)
                    reject(err)
                    return
                }

                if (res.statusCode == 200) {
                    resolve({ statuscode: res.statusCode, queryId: payload.queryId, documentId: payload.documentId, useful: payload.useful })
                }
            })
    })
}


export const feedbackOnAnswer = (payload) => (dispatch, getState, token) => {
    let comment = payload.comment || ''
    return new Promise((resolve, reject) => {
        superagent
        superagent
            .post(comment
                ? `${__API_URI_ARTEMIS__}/feedback/${payload.queryId}`
                : `${__API_URI_ARTEMIS__}/feedback/${payload.queryId}/${payload.useful}`
            )
            .set('Authorization', `Bearer ${token}`)
            .set('Content-Type', 'application/json')
            .set('Platform', 'Web')
            .set('Version', `${__version__}`)
            // .send({
            //     comment: comment // ✅ send as body
            // })
            .end((err, res) => {
                if (err) {
                    console.log('error while handling api/v1/feedback api: ', err)
                    reject(err)
                    return
                }

                if (res.statusCode === 200) {
                    resolve({
                        statuscode: res.statusCode,
                        queryId: payload.queryId,
                        useful: payload.useful
                    })
                }
            })
    })
}


export const feedbackCommentOnAnswer = (payload) => (dispatch, getState, token) => {
    let comment = payload.comment || ''
    return new Promise((resolve, reject) => {
        superagent
        superagent
            .post(`${__API_URI_ARTEMIS__}/feedback/${payload.queryId}`)
            .set('Authorization', `Bearer ${token}`)
            .set('Content-Type', 'application/json')
            .set('Platform', 'Web')
            .set('Version', `${__version__}`)
            .send(JSON.stringify(comment))
            .end((err, res) => {
                if (err) {
                    console.log('error while handling api/v1/feedback api: ', err)
                    reject(err)
                    return
                }

                if (res.statusCode === 200) {
                    resolve({
                        statuscode: res.statusCode,
                        queryId: payload.queryId,
                        useful: payload.useful
                    })
                }
            })
    })
}


export const fetchUserDataSource = () => (dispatch, getState, token) => {
    // if (__NODE_ENV__ != 'production') {
    return new Promise((resolve, reject) => {
        superagent
            .get(`${__API_URI_ARTEMIS__}/UserDataSource`)
            .set('Authorization', `Bearer ${token}`)
            .set('Content-Type', 'application/json')
            .set('Platform', 'Web')
            .set('Version', `${__version__}`)
            .end((err, res) => {
                if (err) {
                    console.log('error while handling api/v1/Query api: ', err)
                    reject(err)
                    return
                }

                if (res.body) {
                    dispatch(storeFetchUserDataSource(res.body))
                    resolve(res.body)
                }
            })
    })
    // }
    // else {
    //     return new Promise((resolve, reject) => {
    //         resolve()
    //     })
    // }
}

export const fetchSuggestedQuestions = (queryID) => (dispatch, getState, token) => {
    // let comment = 'Nice work'
    return new Promise((resolve, reject) => {
        superagent
            .get(`${__API_URI_ARTEMIS__}/questions/${queryID}`)
            .set('Authorization', `Bearer ${token}`)
            .set('Content-Type', 'application/json')
            .set('Platform', 'Web')
            .set('Version', `${__version__}`)
            .end((err, res) => {
                if (err) {
                    console.log('error while handling api/v1/Query api: ', err)
                    reject(err)
                    return
                }

                if (res.body) {
                    resolve(res.body)
                }
            })
    })
}

export const updateSuggestedQuestions = (payload) => (dispatch, getState, token) => {
    // let comment = 'Nice work'
    return new Promise((resolve, reject) => {
        superagent
            .put(`${__API_URI_ARTEMIS__}/UserDataSource`)
            .set('Authorization', `Bearer ${token}`)
            .set('Content-Type', 'application/json')
            .set('Platform', 'Web')
            .set('Version', `${__version__}`)
            .send([
                {
                    dataSourceId: payload.dataSourceId,
                    enabled: payload.enabled
                }
            ]) // ✅ send array payload here
            .end((err, res) => {
                if (err) {
                    console.log('error while handling api/v1/UserDataSource PUT api: ', err);
                    reject(err);
                    return;
                }
                resolve(res);
            });
    })
}
